import { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Download, Send, ToggleLeft, ToggleRight, Users, TrendingUp, Euro } from 'lucide-react';

const API = process.env.REACT_APP_API_URL || '';

export default function Referrers() {
  const [referrers, setReferrers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', phone: '', email: '', commissionRate: 10 });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/api/referrers`);
      setReferrers(r.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      await axios.post(`${API}/api/referrers`, form);
      setForm({ name: '', phone: '', email: '', commissionRate: 10 });
      setShowForm(false);
      await load();
    } catch (err) {
      alert('Erreur: ' + (err.response?.data?.error || err.message));
    } finally { setSaving(false); }
  };

  const downloadQR = async (code) => {
    const r = await axios.get(`${API}/api/referrers/${code}/qr`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([r.data]));
    const a = document.createElement('a');
    a.href = url; a.download = `FlyCash_QR_${code}.png`;
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  };

  const sendKit = async (id) => {
    try {
      await axios.post(`${API}/api/referrers/${id}/send-kit`);
      alert('Kit envoyé par WhatsApp.');
    } catch (err) {
      alert('Erreur: ' + (err.response?.data?.error || err.message));
    }
  };

  const toggle = async (id, active) => {
    await axios.patch(`${API}/api/referrers/${id}/toggle`, { active: !active });
    await load();
  };

  const totalCommission = referrers.reduce((s, r) => s + parseFloat(r.total_commission || 0), 0);
  const totalCases = referrers.reduce((s, r) => s + (r.total_cases || 0), 0);

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Apporteurs d'affaires</h1>
          <p style={styles.subtitle}>Gérez vos commerciaux et leurs liens de parrainage</p>
        </div>
        <button onClick={() => setShowForm(f => !f)} style={styles.addBtn}>
          <Plus size={15} /> Nouvel apporteur
        </button>
      </div>

      {/* Stats */}
      <div style={styles.stats}>
        {[
          { label: 'Apporteurs actifs', value: referrers.filter(r => r.active).length, icon: Users },
          { label: 'Dossiers apportés', value: totalCases, icon: TrendingUp },
          { label: 'Commissions dues', value: `${totalCommission.toFixed(2)}€`, icon: Euro },
        ].map(({ label, value, icon: Icon }) => (
          <div key={label} style={styles.statCard}>
            <Icon size={18} color="#E8920A" />
            <div style={styles.statValue}>{value}</div>
            <div style={styles.statLabel}>{label}</div>
          </div>
        ))}
      </div>

      {/* Create form */}
      {showForm && (
        <div style={styles.form}>
          <h3 style={styles.formTitle}>Nouvel apporteur</h3>
          <div style={styles.formGrid}>
            <div style={styles.field}>
              <label style={styles.label}>Nom complet *</label>
              <input style={styles.input} value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="Jean Dupont" />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>WhatsApp</label>
              <input style={styles.input} value={form.phone}
                onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                placeholder="33612345678" />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Email</label>
              <input style={styles.input} value={form.email}
                onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                placeholder="jean@exemple.com" />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Commission (%)</label>
              <input style={styles.input} type="number" min="1" max="50"
                value={form.commissionRate}
                onChange={e => setForm(f => ({ ...f, commissionRate: parseFloat(e.target.value) }))} />
            </div>
          </div>
          <p style={styles.formNote}>
            Un code unique sera généré automatiquement. Le lien WhatsApp + QR code seront envoyés directement à l'apporteur.
          </p>
          <div style={styles.formActions}>
            <button onClick={() => setShowForm(false)} style={styles.cancelBtn}>Annuler</button>
            <button onClick={handleCreate} disabled={saving || !form.name.trim()} style={styles.saveBtn}>
              {saving ? 'Création...' : 'Créer et envoyer le kit'}
            </button>
          </div>
        </div>
      )}

      {/* List */}
      {loading ? (
        <p style={styles.empty}>Chargement...</p>
      ) : referrers.length === 0 ? (
        <div style={styles.emptyState}>
          <Users size={40} color="#D1D5DB" />
          <p>Aucun apporteur pour l'instant.</p>
        </div>
      ) : (
        <div style={styles.table}>
          <div style={styles.tableHeader}>
            <span>Apporteur</span>
            <span>Code</span>
            <span>Dossiers</span>
            <span>Commission</span>
            <span>Actions</span>
          </div>
          {referrers.map(r => (
            <div key={r.id} style={{ ...styles.tableRow, opacity: r.active ? 1 : 0.5 }}>
              <div>
                <div style={styles.refName}>{r.name}</div>
                <div style={styles.refContact}>
                  {r.phone && `+${r.phone}`}{r.email && ` · ${r.email}`}
                </div>
              </div>
              <div>
                <code style={styles.code}>{r.code}</code>
                <div style={styles.commRate}>{r.commission_rate}% commission</div>
              </div>
              <div style={styles.cases}>
                <span style={styles.casesNum}>{r.total_cases || 0}</span>
                <span style={styles.casesFees}>
                  {r.total_fees ? `${parseFloat(r.total_fees).toFixed(0)}€ générés` : '—'}
                </span>
              </div>
              <div style={styles.commDue}>
                {r.total_commission
                  ? <span style={styles.commAmount}>{parseFloat(r.total_commission).toFixed(2)}€</span>
                  : <span style={styles.commNone}>—</span>}
              </div>
              <div style={styles.actions}>
                <button onClick={() => downloadQR(r.code)} style={styles.iconBtn} title="Télécharger QR code">
                  <Download size={15} />
                </button>
                {r.phone && (
                  <button onClick={() => sendKit(r.id)} style={styles.iconBtn} title="Renvoyer le kit WhatsApp">
                    <Send size={15} />
                  </button>
                )}
                <button onClick={() => toggle(r.id, r.active)} style={styles.iconBtn}
                  title={r.active ? 'Désactiver' : 'Activer'}>
                  {r.active ? <ToggleRight size={18} color="#0D9F57" /> : <ToggleLeft size={18} color="#9CA3AF" />}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const styles = {
  page: { padding: '24px 32px', maxWidth: 1300, margin: '0 auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 },
  title: { fontSize: 22, fontWeight: 800, color: '#0D1B3E', margin: 0 },
  subtitle: { fontSize: 13, color: '#9CA3AF', marginTop: 4 },
  addBtn: { display: 'flex', alignItems: 'center', gap: 6, background: '#0D1B3E', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  stats: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 24 },
  statCard: { background: '#fff', borderRadius: 12, padding: 20, textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' },
  statValue: { fontSize: 24, fontWeight: 800, color: '#0D1B3E', margin: '8px 0 4px' },
  statLabel: { fontSize: 12, color: '#9CA3AF', fontWeight: 600 },
  form: { background: '#fff', borderRadius: 14, padding: '24px 32px', marginBottom: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' },
  formTitle: { fontSize: 15, fontWeight: 800, color: '#0D1B3E', margin: '0 0 16px' },
  formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 12 },
  field: { display: 'flex', flexDirection: 'column', gap: 6 },
  label: { fontSize: 12, fontWeight: 700, color: '#374151' },
  input: { padding: '9px 12px', borderRadius: 8, border: '1px solid #E5E7EB', fontSize: 13, fontFamily: 'inherit', outline: 'none' },
  formNote: { fontSize: 12, color: '#9CA3AF', marginBottom: 16 },
  formActions: { display: 'flex', gap: 8, justifyContent: 'flex-end' },
  cancelBtn: { padding: '9px 16px', borderRadius: 8, border: '1px solid #E5E7EB', background: '#fff', fontSize: 13, cursor: 'pointer' },
  saveBtn: { padding: '9px 16px', borderRadius: 8, background: '#E8920A', color: '#fff', border: 'none', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  table: { background: '#fff', borderRadius: 14, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' },
  tableHeader: { display: 'grid', gridTemplateColumns: '2fr 1.5fr 1fr 1fr 1fr', gap: 16, padding: '12px 20px', background: '#F9FAFB', fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 0.5 },
  tableRow: { display: 'grid', gridTemplateColumns: '2fr 1.5fr 1fr 1fr 1fr', gap: 16, padding: '16px 20px', borderBottom: '1px solid #F3F4F6', alignItems: 'center' },
  refName: { fontSize: 14, fontWeight: 700, color: '#0D1B3E' },
  refContact: { fontSize: 12, color: '#9CA3AF', marginTop: 2 },
  code: { background: '#F3F4F6', padding: '3px 8px', borderRadius: 6, fontSize: 13, fontWeight: 700, color: '#0D1B3E', fontFamily: 'monospace' },
  commRate: { fontSize: 11, color: '#9CA3AF', marginTop: 4 },
  cases: { display: 'flex', flexDirection: 'column' },
  casesNum: { fontSize: 18, fontWeight: 800, color: '#0D1B3E' },
  casesFees: { fontSize: 11, color: '#9CA3AF' },
  commDue: {},
  commAmount: { fontSize: 15, fontWeight: 700, color: '#0D9F57' },
  commNone: { color: '#D1D5DB' },
  actions: { display: 'flex', gap: 8 },
  iconBtn: { background: '#F3F4F6', border: 'none', borderRadius: 8, padding: '7px', cursor: 'pointer', display: 'flex', color: '#374151' },
  empty: { textAlign: 'center', color: '#9CA3AF', padding: 40 },
  emptyState: { textAlign: 'center', color: '#9CA3AF', padding: 60, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 },
};
